/**
 * contractCoreRenderers.tsx — T20.18 Etap 3b (Umowy — strona szczegółów) +
 * R2 silnik formatek (docs/refactor/SILNIK-FORMATEK-ROLLOUT.md) + ADR-0109b
 * (umowy na wspólnym silniku formatek).
 *
 * Prymitywy layout (Field, Section) re-eksportowane z CoreFieldLayout.
 * Helpery widgetów: TextInput, Textarea, SelectInput, CheckboxField — używane
 * przez `CONTRACT_CORE_RENDERERS` niżej (full renderer override — konsumowane przez
 * `contractEngineConfig.ts`): 13 pól wspólnych dokumentu + 13 pól umowy
 * (`document_contract_details` — numer, status umowy, przedmiot, okres, wartość,
 * z regułami poufności). `ContractFormData`/`EMPTY_CONTRACT_FORM` = stan
 * formularza, współdzielony przez ContractDetail/engine config.
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_DOCUMENT_STATUSES,
  CONTRACT_PERIOD_UNITS,
  CONTRACT_STATUSES,
  DOCUMENT_URGENCIES,
} from '@saas/shared';

import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS_MUTED_DISABLED } from '@/components/coreRendererClasses';
import { Dropdown } from '@/components/Dropdown';
import { Field, Section } from '@/components/kartoteki/CoreFieldLayout';
import type { FieldRenderer } from '@/components/kartoteki/engine/engine-config';
import { UserPicker } from '@/components/ui/UserPicker';
import { UserTablePickerButton } from '@/components/ui/UserTablePickerButton';
import {
  contractDocumentStatusLabel,
  contractPeriodUnitLabel,
  contractStatusLabel,
  documentUrgencyLabel,
} from '@/lib/documents/document-select-labels';

import { makeDocumentCategoryRenderer } from './documentCategoryRenderer';

export { Field, Section };

// Kody statusu dokumentu, statusu umowy, pilności i jednostki czasu trwania pochodzą z
// `@saas/shared` (jedno źródło dla formularza, kolumny i filtra listy); nazwy kodów — z
// `lib/documents/document-select-labels` (te same funkcje czyta kolumna listy i jej filtr).

// ─── Klasy CSS wspólne ───────────────────────────────────────────────────────

export const inputClass = CORE_INPUT_CLASS_MUTED_DISABLED;

// ─── Prymitywy inputów ────────────────────────────────────────────────────────

export function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput variant="mutedDisabled" {...props} />;
}

export function Textarea({
  value,
  onChange,
  disabled,
  testId,
  rows = 3,
  maxLength,
  placeholder,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      maxLength={maxLength}
      placeholder={placeholder}
      data-testid={testId}
      aria-invalid={invalid ? true : undefined}
      className={invalid ? `${inputClass} border-red-500 ring-1 ring-red-500` : inputClass}
    />
  );
}

export function SelectInput({
  value,
  onChange,
  options,
  labelFor,
  disabled,
  testId,
  withEmpty = false,
  emptyLabel = '— wybierz —',
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  /** Nazwa kodu na liście; bez niej opcja pokazuje sam kod. Kod spoza listy wraca surowy. */
  labelFor?: (code: string, t: (key: string) => string) => string;
  disabled?: boolean;
  testId?: string;
  withEmpty?: boolean;
  emptyLabel?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  // Hook w pod-komponencie — renderery pól (`FieldRenderer`) są czystymi funkcjami.
  const { t } = useTranslation();
  return (
    <Dropdown
      options={[
        ...(withEmpty ? [{ value: '', label: emptyLabel }] : []),
        ...options.map((opt) => ({ value: opt, label: labelFor ? labelFor(opt, t) : opt })),
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? 'border-red-500 ring-1 ring-red-500' : undefined}
    />
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  disabled,
  testId,
  invalid,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  testId?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={testId}
        aria-invalid={invalid ? true : undefined}
        className={
          invalid
            ? 'h-4 w-4 rounded border-red-500 ring-1 ring-red-500 accent-accent-600 disabled:cursor-not-allowed'
            : 'h-4 w-4 rounded border-border-secondary accent-accent-600 disabled:cursor-not-allowed'
        }
      />
      {label}
    </label>
  );
}

// ─── Typy re-exportowane ─────────────────────────────────────────────────────

export interface ContractFormData {
  // — pola dokumentu (core) —
  title: string | null;
  status: string;
  urgency: string;
  description: string | null;
  notes: string | null;
  documentDate: string | null;
  source: string;
  externalId: string | null;
  externalSource: string | null;
  rejectionReason: string | null;
  /** R2 (silnik formatek): read-only, auto-generowany przez backend — mirror BareDocFormData. */
  systemNumber: string | null;
  ownerUserId: string | null;
  /** Kategoria dokumentu (oś `record_categories`, kartoteka_key='documents'). Jedna, opcjonalna. */
  categoryId: string | null;
  documentTypeId?: string | null;
  // — pola satellite (document_contract_details) —
  contractNumber: string | null;
  contractNumberMissing: boolean;
  periodFrom: string | null;
  periodTo: string | null;
  periodIndefinite: boolean;
  periodDurationValue: number | null;
  periodDurationUnit: string | null;
  contractValue: string | null;
  contractValueDescription: string | null;
  contractStatus: string | null;
  subjectMatter: string | null;
  subjectMatterConfidential: boolean;
  valueConfidential: boolean;
}

export const EMPTY_CONTRACT_FORM: ContractFormData = {
  title: null,
  status: 'draft',
  urgency: 'normal',
  description: null,
  notes: null,
  documentDate: null,
  source: 'manual',
  externalId: null,
  externalSource: null,
  rejectionReason: null,
  systemNumber: null,
  ownerUserId: null,
  categoryId: null,
  documentTypeId: null,
  contractNumber: null,
  contractNumberMissing: false,
  periodFrom: null,
  periodTo: null,
  periodIndefinite: false,
  periodDurationValue: null,
  periodDurationUnit: null,
  contractValue: null,
  contractValueDescription: null,
  contractStatus: null,
  subjectMatter: null,
  subjectMatterConfidential: false,
  valueConfidential: false,
};

// ─── Silnik formatek — renderery pól wspólnych dokumentu i pól umowy, razem 26 (R2, docs/refactor/SILNIK-FORMATEK-ROLLOUT.md) ──
//
// Mirror `invoiceEngineConfig.ts` (full renderer override), NIE pilota
// commercial/internal (native delegation): istniejące widgety Umowy odbiegają
// od generycznego ExtFieldValueInput per-fieldType — placeholder na title/
// systemNumber, `description` jako single-line TextInput (nie Textarea mimo
// fieldType='text' w seedzie), `source` jako wolny tekst (nie select mimo
// fieldType='select' w seedzie — rozjazd istniał już przed silnikiem, nie
// naprawiamy go tutaj), `status`/`urgency` z WŁASNYM enumem statusu
// (CONTRACT_DOCUMENT_STATUSES z `@saas/shared` — inny niż STANDARD_DOCUMENT_STATUSES
// w documentCoreRenderers.tsx). O tym, które pola są na formularzu, decyduje układ (zakładka „Dane"
// schodzi przez `RecursiveFormRenderer`, jak u faktur). Wyjątek przeniesiony 1:1 z dawnej
// ręcznej zakładki: external_id/external_source/rejection_reason przy TWORZENIU nic nie
// rysują (`isCreate` → null) — zapis tworzenia umowy ich nie przyjmuje (POST /contracts
// nie ma tych pól, payload w ContractDetail ich nie wysyła), więc wpisana wartość by przepadła.

const systemNumberRenderer: FieldRenderer<ContractFormData> = ({ form, isCreate, t }) => (
  <Field label={t('tenant.documents.contracts.detail.field.systemNumber')} required={false}>
    <TextInput
      testId="contract-field-systemNumber"
      value={form.systemNumber ?? ''}
      onChange={() => {}}
      disabled
      placeholder={isCreate ? t('tenant.documents.contracts.detail.systemNumberPlaceholder') : ''}
    />
  </Field>
);

const titleRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.title')}
    required={false}
    error={getError?.('title')}
  >
    <TextInput
      testId="contract-field-title"
      value={form.title ?? ''}
      onChange={(v) => setField({ title: v || null })}
      disabled={!editable}
      maxLength={500}
      placeholder={t('tenant.documents.contracts.detail.field.titlePlaceholder')}
      invalid={hasError?.('title')}
    />
  </Field>
);

const statusRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.status')}
    required={true}
    error={getError?.('status')}
  >
    <SelectInput
      testId="contract-field-status"
      value={form.status}
      onChange={(v) => setField({ status: v })}
      options={CONTRACT_DOCUMENT_STATUSES}
      labelFor={contractDocumentStatusLabel}
      disabled={!editable}
      invalid={hasError?.('status')}
    />
  </Field>
);

const documentDateRenderer: FieldRenderer<ContractFormData> = ({
  field,
  form,
  setField,
  editable,
  hasError,
  getError,
  isFieldExtraRequired,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.documentDate')}
    required={isFieldExtraRequired(field)}
    error={getError?.('documentDate')}
  >
    <TextInput
      testId="contract-field-documentDate"
      type="date"
      value={form.documentDate ?? ''}
      onChange={(v) => setField({ documentDate: v || null })}
      disabled={!editable}
      invalid={hasError?.('documentDate')}
    />
  </Field>
);

const descriptionRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  // R2 (ujednolicenie, decyzja Alexandra): wielowierszowe (Textarea ~3 wiersze),
  // spójne z pilotem/pismami/fakturami. maxLength=500 i kształt zapisu (`v || null`)
  // bez zmian względem poprzedniego jednowierszowego inputu.
  <Field
    label={t('tenant.documents.contracts.detail.field.description')}
    required={false}
    colSpan={2}
    error={getError?.('description')}
  >
    <Textarea
      testId="contract-field-description"
      value={form.description ?? ''}
      onChange={(v) => setField({ description: v || null })}
      disabled={!editable}
      rows={3}
      maxLength={500}
      invalid={hasError?.('description')}
    />
  </Field>
);

const urgencyRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.urgency')}
    required={true}
    error={getError?.('urgency')}
  >
    <SelectInput
      testId="contract-field-urgency"
      value={form.urgency}
      onChange={(v) => setField({ urgency: v })}
      options={DOCUMENT_URGENCIES}
      labelFor={documentUrgencyLabel}
      disabled={!editable}
      invalid={hasError?.('urgency')}
    />
  </Field>
);

const ownerUserIdRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  companyId,
  t,
}) => (
  <Field label={t('tenant.documents.contracts.detail.field.ownerUserId')} required={false}>
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <UserPicker
          value={form.ownerUserId}
          onChange={(v) => setField({ ownerUserId: v })}
          companyId={companyId}
          disabled={!editable}
          data-testid="contract-field-ownerUserId"
        />
      </div>
      <UserTablePickerButton
        value={form.ownerUserId}
        onPick={(id) => setField({ ownerUserId: id })}
        companyId={companyId}
        disabled={!editable}
        data-testid="contract-field-ownerUserId-open-picker"
      />
    </div>
  </Field>
);

const notesRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.notes')}
    required={false}
    colSpan={2}
    error={getError?.('notes')}
  >
    <Textarea
      testId="contract-field-notes"
      value={form.notes ?? ''}
      onChange={(v) => setField({ notes: v || null })}
      disabled={!editable}
      rows={3}
      invalid={hasError?.('notes')}
    />
  </Field>
);

const rejectionReasonRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  isCreate,
  hasError,
  getError,
  t,
}) =>
  isCreate ? null : (
    <Field
      label={t('tenant.documents.contracts.detail.field.rejectionReason')}
      required={false}
      colSpan={2}
      error={getError?.('rejectionReason')}
    >
      <Textarea
        testId="contract-field-rejectionReason"
        value={form.rejectionReason ?? ''}
        onChange={(v) => setField({ rejectionReason: v || null })}
        disabled={!editable}
        rows={2}
        invalid={hasError?.('rejectionReason')}
      />
    </Field>
  );

const sourceRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.source')}
    required={false}
    error={getError?.('source')}
  >
    <TextInput
      testId="contract-field-source"
      value={form.source}
      onChange={(v) => setField({ source: v })}
      disabled={!editable}
      maxLength={50}
      invalid={hasError?.('source')}
    />
  </Field>
);

const externalIdRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  isCreate,
  hasError,
  getError,
  t,
}) =>
  isCreate ? null : (
    <Field
      label={t('tenant.documents.contracts.detail.field.externalId')}
      required={false}
      error={getError?.('externalId')}
    >
      <TextInput
        testId="contract-field-externalId"
        value={form.externalId ?? ''}
        onChange={(v) => setField({ externalId: v || null })}
        disabled={!editable}
        maxLength={100}
        invalid={hasError?.('externalId')}
      />
    </Field>
  );

const externalSourceRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  isCreate,
  hasError,
  getError,
  t,
}) =>
  isCreate ? null : (
    <Field
      label={t('tenant.documents.contracts.detail.field.externalSource')}
      required={false}
      error={getError?.('externalSource')}
    >
      <TextInput
        testId="contract-field-externalSource"
        value={form.externalSource ?? ''}
        onChange={(v) => setField({ externalSource: v || null })}
        disabled={!editable}
        maxLength={100}
        invalid={hasError?.('externalSource')}
      />
    </Field>
  );

// Kategoria (oś `record_categories`, kartoteka_key='documents') — renderer WSPÓLNY dla
// wszystkich 7 klas dokumentów, patrz documentCategoryRenderer.tsx.
const categoryRenderer = makeDocumentCategoryRenderer<ContractFormData>();

// ─── Pola umowy (`document_contract_details`) — ADR-0109b ─────────────────────
//
// Przeniesione 1:1 z dawnej ręcznej zakładki „Dane" umowy: te same kontrolki, limity,
// blokady i reguły między polami. Reguły czyszczenia działają w stanie formularza
// (jeden `setField` z kilkoma polami): poufność wartości → `contractValue=null`,
// poufność przedmiotu → `subjectMatter=null`, brak numeru → `contractNumber=null`,
// bezterminowo → `periodFrom`/`periodTo=null`. Drugi pas czyszczenia przy zapisie
// zostaje w payloadzie `ContractDetail`. Klucz błędu walidacji = klucz stanu
// (camelCase), jak dotąd.

const contractNumberRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.contractNumber')}
    required={false}
    error={getError?.('contractNumber')}
  >
    <TextInput
      testId="contract-field-contractNumber"
      value={form.contractNumber ?? ''}
      onChange={(v) => setField({ contractNumber: v || null })}
      disabled={!editable || form.contractNumberMissing}
      maxLength={100}
      placeholder={
        form.contractNumberMissing
          ? t('tenant.documents.contracts.detail.field.contractNumberMissingPlaceholder')
          : ''
      }
      invalid={hasError?.('contractNumber')}
    />
  </Field>
);

const contractNumberMissingRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.contractNumberMissing')}
    required={false}
  >
    <CheckboxField
      label={t('tenant.documents.contracts.detail.field.contractNumberMissingLabel')}
      checked={form.contractNumberMissing}
      onChange={(v) =>
        setField({
          contractNumberMissing: v,
          contractNumber: v ? null : form.contractNumber,
        })
      }
      disabled={!editable}
      testId="contract-field-contractNumberMissing"
      invalid={hasError?.('contractNumberMissing')}
    />
  </Field>
);

const contractStatusRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.contractStatus')}
    required={false}
    error={getError?.('contractStatus')}
  >
    <SelectInput
      testId="contract-field-contractStatus"
      value={form.contractStatus ?? ''}
      onChange={(v) => setField({ contractStatus: v || null })}
      options={CONTRACT_STATUSES}
      labelFor={contractStatusLabel}
      disabled={!editable}
      withEmpty
      emptyLabel={t('tenant.documents.contracts.detail.field.contractStatusEmpty')}
      invalid={hasError?.('contractStatus')}
    />
  </Field>
);

const subjectMatterRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.subjectMatter')}
    required={false}
    colSpan={2}
    error={getError?.('subjectMatter')}
  >
    <Textarea
      testId="contract-field-subjectMatter"
      value={form.subjectMatter ?? ''}
      onChange={(v) => setField({ subjectMatter: v || null })}
      disabled={!editable || form.subjectMatterConfidential}
      rows={3}
      placeholder={
        form.subjectMatterConfidential
          ? t('tenant.documents.contracts.detail.field.subjectMatterConfidentialPlaceholder')
          : ''
      }
      invalid={hasError?.('subjectMatter')}
    />
  </Field>
);

const subjectMatterConfidentialRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.subjectMatterConfidential')}
    required={false}
  >
    <CheckboxField
      label={t('tenant.documents.contracts.detail.field.subjectMatterConfidentialLabel')}
      checked={form.subjectMatterConfidential}
      onChange={(v) =>
        setField({
          subjectMatterConfidential: v,
          subjectMatter: v ? null : form.subjectMatter,
        })
      }
      disabled={!editable}
      testId="contract-field-subjectMatterConfidential"
      invalid={hasError?.('subjectMatterConfidential')}
    />
  </Field>
);

// Daty okresu mogą być podstawą numeracji typu umowy (DOCUMENT_CORE_DATE_FIELDS.contract) —
// gwiazdka „wymagane", gdy wybrany typ numeruje po tym polu (jak `documentDateRenderer`).
const periodFromRenderer: FieldRenderer<ContractFormData> = ({
  field,
  form,
  setField,
  editable,
  hasError,
  getError,
  isFieldExtraRequired,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.periodFrom')}
    required={isFieldExtraRequired(field)}
    error={getError?.('periodFrom')}
  >
    <TextInput
      testId="contract-field-periodFrom"
      type="date"
      value={form.periodFrom ?? ''}
      onChange={(v) => setField({ periodFrom: v || null })}
      disabled={!editable || form.periodIndefinite}
      invalid={hasError?.('periodFrom')}
    />
  </Field>
);

const periodToRenderer: FieldRenderer<ContractFormData> = ({
  field,
  form,
  setField,
  editable,
  hasError,
  getError,
  isFieldExtraRequired,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.periodTo')}
    required={isFieldExtraRequired(field)}
    error={getError?.('periodTo')}
  >
    <TextInput
      testId="contract-field-periodTo"
      type="date"
      value={form.periodTo ?? ''}
      onChange={(v) => setField({ periodTo: v || null })}
      disabled={!editable || form.periodIndefinite}
      invalid={hasError?.('periodTo')}
    />
  </Field>
);

const periodIndefiniteRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  t,
}) => (
  <Field label={t('tenant.documents.contracts.detail.field.periodIndefinite')} required={false}>
    <CheckboxField
      label={t('tenant.documents.contracts.detail.field.periodIndefiniteLabel')}
      checked={form.periodIndefinite}
      onChange={(v) =>
        setField({
          periodIndefinite: v,
          periodFrom: v ? null : form.periodFrom,
          periodTo: v ? null : form.periodTo,
        })
      }
      disabled={!editable}
      testId="contract-field-periodIndefinite"
      invalid={hasError?.('periodIndefinite')}
    />
  </Field>
);

const periodDurationValueRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.periodDurationValue')}
    required={false}
    error={getError?.('periodDurationValue')}
  >
    <TextInput
      testId="contract-field-periodDurationValue"
      type="number"
      min="1"
      step="1"
      value={String(form.periodDurationValue ?? '')}
      onChange={(v) => setField({ periodDurationValue: v ? parseInt(v, 10) : null })}
      disabled={!editable}
      invalid={hasError?.('periodDurationValue')}
    />
  </Field>
);

const periodDurationUnitRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.periodDurationUnit')}
    required={false}
    error={getError?.('periodDurationUnit')}
  >
    <SelectInput
      testId="contract-field-periodDurationUnit"
      value={form.periodDurationUnit ?? ''}
      onChange={(v) => setField({ periodDurationUnit: v || null })}
      options={CONTRACT_PERIOD_UNITS}
      labelFor={contractPeriodUnitLabel}
      disabled={!editable}
      withEmpty
      emptyLabel={t('tenant.documents.contracts.detail.field.periodDurationUnitEmpty')}
      invalid={hasError?.('periodDurationUnit')}
    />
  </Field>
);

const contractValueRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.contractValue')}
    required={false}
    error={getError?.('contractValue')}
  >
    <TextInput
      testId="contract-field-contractValue"
      type="number"
      step="0.01"
      min="0"
      value={form.contractValue ?? ''}
      onChange={(v) => setField({ contractValue: v || null })}
      disabled={!editable || form.valueConfidential}
      placeholder={
        form.valueConfidential
          ? t('tenant.documents.contracts.detail.field.contractValueConfidentialPlaceholder')
          : ''
      }
      invalid={hasError?.('contractValue')}
    />
  </Field>
);

const valueConfidentialRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  t,
}) => (
  <Field label={t('tenant.documents.contracts.detail.field.valueConfidential')} required={false}>
    <CheckboxField
      label={t('tenant.documents.contracts.detail.field.valueConfidentialLabel')}
      checked={form.valueConfidential}
      onChange={(v) =>
        setField({
          valueConfidential: v,
          contractValue: v ? null : form.contractValue,
        })
      }
      disabled={!editable}
      testId="contract-field-valueConfidential"
      invalid={hasError?.('valueConfidential')}
    />
  </Field>
);

const contractValueDescriptionRenderer: FieldRenderer<ContractFormData> = ({
  form,
  setField,
  editable,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.contracts.detail.field.contractValueDescription')}
    required={false}
    colSpan={2}
    error={getError?.('contractValueDescription')}
  >
    <TextInput
      testId="contract-field-contractValueDescription"
      value={form.contractValueDescription ?? ''}
      onChange={(v) => setField({ contractValueDescription: v || null })}
      disabled={!editable}
      maxLength={500}
      invalid={hasError?.('contractValueDescription')}
    />
  </Field>
);

/**
 * Mapa slug → renderer: najpierw 13 pól wspólnych dokumentu w kolejności seeda, potem
 * 13 pól umowy (`document_contract_details`, ADR-0109b). Klucze = `coreFieldSlugs`
 * configu (synteza zakładki „Dane", gdy brak układu).
 */
export const CONTRACT_CORE_RENDERERS: Record<string, FieldRenderer<ContractFormData>> = {
  system_number: systemNumberRenderer,
  title: titleRenderer,
  status: statusRenderer,
  document_date: documentDateRenderer,
  description: descriptionRenderer,
  urgency: urgencyRenderer,
  owner_user_id: ownerUserIdRenderer,
  category_id: categoryRenderer,
  notes: notesRenderer,
  rejection_reason: rejectionReasonRenderer,
  source: sourceRenderer,
  external_id: externalIdRenderer,
  external_source: externalSourceRenderer,
  // Pola umowy (ADR-0109b)
  contract_number: contractNumberRenderer,
  contract_number_missing: contractNumberMissingRenderer,
  contract_status: contractStatusRenderer,
  subject_matter: subjectMatterRenderer,
  subject_matter_confidential: subjectMatterConfidentialRenderer,
  period_from: periodFromRenderer,
  period_to: periodToRenderer,
  period_indefinite: periodIndefiniteRenderer,
  period_duration_value: periodDurationValueRenderer,
  period_duration_unit: periodDurationUnitRenderer,
  contract_value: contractValueRenderer,
  value_confidential: valueConfidentialRenderer,
  contract_value_description: contractValueDescriptionRenderer,
};

// Pomocniczy typ dla pola label w JSX (re-export for convenience)
export type { ReactNode };
